/**
 * Product Portfolio Gallery — custom element
 *
 * Manages tab switching and horizontal card scrolling for the
 * sections/product-portfolio-gallery.liquid section.
 *
 * How it works:
 *   - Two collection tracks ("new-arrivals" and "best-sellers") are pre-rendered
 *     server-side. Only the active track is visible (CSS .is-active).
 *   - Tab clicks swap the active track and reset the scroll position.
 *   - Prev / Next arrows slide the active track horizontally using CSS transform.
 *
 * This file is loaded by the Liquid section via script_tag and has no
 * external dependencies.
 */

class ProductPortfolioGallery extends HTMLElement {
  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  connectedCallback() {
    // connectedCallback fires when the element's opening tag is parsed —
    // before any child elements exist in the DOM. Deferring with setTimeout
    // ensures all children are available when init() runs.
    setTimeout(() => this.init(), 0);
  }

  init() {
    /**
     * @type {number} Number of cards to scroll per arrow click.
     * Read from the data-cards-per-view attribute set by Liquid.
     */
    this.cardsPerView = parseInt(this.dataset.cardsPerView, 10) || 4;

    /**
     * @type {string} Identifier of the currently active tab (matches data-collection).
     */
    this.activeTabName = "new-arrivals";

    /**
     * @type {number} Current horizontal scroll offset in pixels (negative = scrolled right).
     */
    this.scrollOffset = 0;

    // Collect references to interactive elements
    this.tabButtons = Array.from(
      this.querySelectorAll(".product-portfolio-gallery__tab"),
    );
    this.collectionTracks = Array.from(
      this.querySelectorAll(".product-portfolio-gallery__track"),
    );
    this.prevButton = this.querySelector(".product-portfolio-gallery__prev");
    this.nextButton = this.querySelector(".product-portfolio-gallery__next");
    this.viewAllLink = this.querySelector(
      ".product-portfolio-gallery__view-all-link",
    );

    this.attachEventListeners();
    this.setupHoverVideos();
    this.updateNavigationState();
  }

  // -------------------------------------------------------------------------
  // Initialisation helpers
  // -------------------------------------------------------------------------

  /**
   * Binds click handlers to tab buttons and navigation arrows.
   */
  attachEventListeners() {
    this.tabButtons.forEach((tab) => {
      tab.addEventListener("click", () => this.handleTabClick(tab));
    });

    if (this.prevButton) {
      this.prevButton.addEventListener("click", () => this.scrollCards("prev"));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", () => this.scrollCards("next"));
    }
  }

  /**
   * Plays the hover video (when present) while the mouse is over a card, and
   * pauses + rewinds it on leave. Mouse-only, matching the native card-gallery
   * hover behaviour — touch devices never trigger it. Image-only hover media
   * needs no JS; it is handled purely by CSS.
   */
  setupHoverVideos() {
    const cards = this.querySelectorAll(".card-product");

    cards.forEach((card) => {
      const video = card.querySelector("video.card-product__media-hover");
      if (!video) return;

      card.addEventListener("pointerenter", (event) => {
        if (event.pointerType && event.pointerType !== "mouse") return;
        video.play().catch(() => {});
      });

      card.addEventListener("pointerleave", (event) => {
        if (event.pointerType && event.pointerType !== "mouse") return;
        video.pause();
        video.currentTime = 0;
      });
    });
  }

  // -------------------------------------------------------------------------
  // Tab switching
  // -------------------------------------------------------------------------

  /**
   * Switches the visible collection track to the one matching the clicked tab.
   * Resets scroll position when changing tabs.
   *
   * @param {HTMLButtonElement} clickedTab - The tab button that was clicked
   */
  handleTabClick(clickedTab) {
    const targetTabName = clickedTab.dataset.tab;

    // Update active styling on tab buttons
    this.tabButtons.forEach((tab) => {
      tab.classList.toggle("is-active", tab.dataset.tab === targetTabName);
    });

    // Show the matching collection track and hide the others
    this.collectionTracks.forEach((track) => {
      const isTargetTrack = track.dataset.collection === targetTabName;
      track.classList.toggle("is-active", isTargetTrack);

      // Reset transform so the new tab always starts from the beginning
      if (isTargetTrack) {
        track.style.transform = "translateX(0)";
      }
    });

    this.activeTabName = targetTabName;
    this.scrollOffset = 0;
    this.updateViewAllLink();
    this.updateNavigationState();
  }

  /**
   * Updates the View All link href to match the active tab's collection URL.
   */
  updateViewAllLink() {
    if (!this.viewAllLink) return;
    const hrefKey = `href${this.activeTabName
      ?.split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join("")}`;
    const href = this.viewAllLink.dataset[hrefKey];
    if (href) this.viewAllLink.href = href;
  }

  // -------------------------------------------------------------------------
  // Horizontal scrolling
  // -------------------------------------------------------------------------

  /**
   * Returns the currently visible collection track element.
   *
   * @returns {HTMLElement|null}
   */
  getActiveTrack() {
    return this.querySelector(".product-portfolio-gallery__track.is-active");
  }

  /**
   * Scrolls the active track left or right by the width of cardsPerView cards.
   *
   * @param {'prev'|'next'} direction - Scroll direction
   */
  scrollCards(direction) {
    const activeTrack = this.getActiveTrack();
    if (!activeTrack) return;

    const cards = activeTrack.querySelectorAll(".card-product");
    if (cards.length === 0) return;

    const cardWidth = cards[0].offsetWidth;
    const gapBetweenCards = 20; // Must match the gap in product-portfolio-gallery.css
    const scrollAmount = (cardWidth + gapBetweenCards) * this.cardsPerView;

    const totalCards = cards.length;
    const maxScrollOffset =
      -(totalCards - this.cardsPerView) * (cardWidth + gapBetweenCards);

    if (direction === "next") {
      this.scrollOffset -= scrollAmount;
      // Clamp so we never scroll past the last card
      if (this.scrollOffset < maxScrollOffset) {
        this.scrollOffset = maxScrollOffset;
      }
    } else {
      this.scrollOffset += scrollAmount;
      // Clamp so we never scroll before the first card
      if (this.scrollOffset > 0) {
        this.scrollOffset = 0;
      }
    }

    activeTrack.style.transform = `translateX(${this.scrollOffset}px)`;
    this.updateNavigationState();
  }

  /**
   * Enables or disables prev / next buttons based on the current scroll position
   * and how many cards remain off-screen in each direction.
   */
  updateNavigationState() {
    const activeTrack = this.getActiveTrack();
    if (!activeTrack || !this.prevButton || !this.nextButton) return;

    const cards = activeTrack.querySelectorAll(".card-product");
    const totalCards = cards.length;

    // Prev is disabled when we're at the beginning (scrollOffset is 0 or positive)
    this.prevButton.disabled = this.scrollOffset >= 0;

    // Next is disabled when all cards fit in the viewport or we've reached the end
    const cardWidth = cards[0] ? cards[0].offsetWidth : 250;
    const gapBetweenCards = 20;
    const maxScrollOffset =
      -(totalCards - this.cardsPerView) * (cardWidth + gapBetweenCards);
    this.nextButton.disabled =
      this.scrollOffset <= maxScrollOffset || totalCards <= this.cardsPerView;
  }
}

// Guard against the element being defined twice when Shopify re-renders sections
if (!customElements.get("product-portfolio-gallery")) {
  customElements.define("product-portfolio-gallery", ProductPortfolioGallery);
}
